/**
 * The document table, shared by the library and the matter's Documents tab.
 *
 * Two columns here are domain, not decoration:
 *
 * SCAN STATUS. Every upload is scanned. A document is not downloadable until the scan
 * returns clean, and `is_downloadable` is derived at read time from the current
 * version rather than stored — so there is no flag that could have gone stale while
 * the file sat quarantined. The list shows the scan state plainly; an `infected` file
 * reads as danger and nothing else.
 *
 * VISIBILITY. `internal` and `client` are rendered with a text label, not a colour.
 * Somebody deciding whether to share a counsel advice note is the CR-25 leak class,
 * and a coloured dot is not enough to stop them.
 */

import type { DocumentListItem } from '@legalcrm/api';
import { Badge, StatusPill, Table, Tooltip, statusTone, type Column } from '@legalcrm/ui';

import { formatDate, humanise } from '@/lib/format';

const SCAN_TONES = {
  clean: 'success',
  scanning: 'info',
  pending: 'neutral',
  infected: 'danger',
  failed: 'danger',
} as const;

const SCAN_LABELS = {
  clean: 'Clean',
  scanning: 'Scanning',
  pending: 'Queued',
  infected: 'Infected',
  failed: 'Scan failed',
} as const;

export interface DocumentsTableProps {
  rows: DocumentListItem[];
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  onRowClick?: (row: DocumentListItem) => void;
  pagination?: {
    hasNext: boolean;
    hasPrevious: boolean;
    onNext: () => void;
    onPrevious: () => void;
    summary?: string;
  };
  emptyTitle?: string;
  emptyDescription?: string;
  /** Off where every row is the same client's, e.g. inside one matter. */
  showClient?: boolean;
}

export function DocumentsTable({
  rows,
  loading,
  error,
  onRetry,
  onRowClick,
  pagination,
  emptyTitle = 'No documents',
  emptyDescription = 'Nothing matches these filters.',
  showClient = true,
}: DocumentsTableProps) {
  const columns: Column<DocumentListItem>[] = [
    {
      key: 'name',
      header: 'Title',
      render: (row) => (
        <div className="min-w-0">
          <p className="flex min-w-0 items-center gap-2">
            {/* A fixed width, so a long file name is cut short instead of widening the
                column; the whole name is one hover (or focus) away. */}
            <Tooltip content={row.name} align="start" className="min-w-0">
              <span className="block max-w-[16rem] truncate text-ink">{row.name}</span>
            </Tooltip>
            {row.version_count > 1 && (
              // A re-sent file keeps its original record, so without this it looks old.
              <Badge tone="info" size="sm" className="shrink-0">
                Version {row.version_count}
              </Badge>
            )}
          </p>
          <p className="truncate text-xs text-ink-muted">{humanise(row.kind)}</p>
        </div>
      ),
    },
    ...(showClient
      ? [
          {
            key: 'client_name',
            header: 'Client',
            hideOnMobile: true,
            render: (row: DocumentListItem) => (
              <span className="block max-w-[12rem] truncate text-ink">{row.client_name}</span>
            ),
          },
        ]
      : []),
    {
      key: 'status',
      header: 'Review',
      hideOnMobile: true,
      render: (row) => (
        <StatusPill label={humanise(row.status)} tone={statusTone(row.status)} />
      ),
    },
    {
      key: 'scan_status',
      header: 'Scan',
      hideOnMobile: true,
      render: (row) => (
        <Badge tone={SCAN_TONES[row.scan_status] ?? 'neutral'} size="sm" dot>
          {SCAN_LABELS[row.scan_status] ?? humanise(row.scan_status)}
        </Badge>
      ),
    },
    {
      key: 'visibility',
      header: 'Visibility',
      render: (row) => (
        <Badge
          tone={row.visibility === 'internal' ? 'internal' : 'client-facing'}
          size="sm"
        >
          {row.visibility === 'internal' ? 'Internal only' : 'Client can see this'}
        </Badge>
      ),
    },
    {
      key: 'latest_upload_at',
      header: 'Last upload',
      align: 'right',
      hideOnMobile: true,
      render: (row) => (
        <span className="tabular-nums">{formatDate(row.latest_upload_at ?? row.created_at)}</span>
      ),
    },
  ];

  return (
    <Table
      caption="Documents"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      loading={loading}
      error={error}
      onRetry={onRetry}
      onRowClick={onRowClick}
      emptyTitle={emptyTitle}
      emptyDescription={emptyDescription}
      pagination={pagination}
    />
  );
}
